import { useEffect, useState } from "react";
import * as React from "react";
import _, { debounce } from "lodash";
import cx from "classnames";
import { match } from "ts-pattern";
import Geosuggest, { Suggest } from "react-geosuggest";
import { parseISO } from "date-fns";
import { AsyncPaginate } from "react-select-async-paginate";
import type { GroupBase, MultiValue, OptionsOrGroups, SingleValue } from "react-select";
import useRouter from "use-react-router";

import { AnswerType, Gig, TemperatureUnit } from "gql-gen";
import cancelIcon from "assets/cancel.svg";

import Icon from "components/Icon";
import Text from "components/LegacyText";
import Form from "components/Form";
import BaseFormElement from "components/Form/BaseFormElement";
import { IFile } from "components/Form/FilePicker";
import RadioGroup from "components/RadioGroup";
import Switch from "components/Switch";
import RadioButton from "components/RadioButton";
import ImageGroup from "components/ImageGroup";
import Clickable from "components/Clickable";
import { AnswerState, GigReportAnswer } from "interfaces/ReportStep";
import { ReportQuestion, QuestionConfig, DecimalSettings } from "interfaces/ReportQuestion";
import { CalendarDropdown } from "components/Calendar/CalendarDropdown";
import { ContactQuestionFieldValue } from "components/Form/ContactBox";
import { AgreementSignatureQuestionValue } from "components/Form/AgreementBox";
import variables from "styles/variables";
import { Desktop } from "components/Layout/Responsive";
import { Markdown } from "components/Markdown";
import { toKelvin } from "utilities/temperature";
import SearchableSelect from "components/LegacySearchable/SearchableSelect";
import SearchableMultiSelect from "components/LegacySearchable/SearchableMultiSelect";
import { getRustBackendUrl } from "utilities/getServerRootUrl";
import { getJwt } from "utilities/authentication";

import styles from "./styles.scss";

import { StepState, ReportQuestionWChildren } from "../StepState";
import ProductInfoSection from "../ProductInfoSection";
import StepItem from "../StepItem";
import { CONFIG } from "../config";

interface StepFormProps {
  gig: Gig;
  disabled: boolean;
  step: StepState;
  setAnswer: (qId: string, answer: GigReportAnswer<any>, debounce: boolean) => void;
  returnToChecklist: (goToNext: boolean) => void;
  goToCheckin: () => void;
  verifyReport?: boolean;
  isMobile?: boolean;
  allAnswered: boolean;
}

const returnedObj = <T extends any>(value: T, state: AnswerState) => ({ value, state });
const answeredWithVal = <T extends any>(value: T) => returnedObj(value, "answered");
const nullAndPending = returnedObj(null, "pending");
const nullAndAnswered = returnedObj(null, "answered");
const nullNotApplicable = returnedObj(null, "na");
const answeredButPending = <T extends any>(value: T) => returnedObj(value, "pending");

function componentStateReducer(answerType: AnswerType, answerValue: any, answerState: AnswerState) {
  if (answerState === "na") {
    return nullNotApplicable;
  }
  switch (answerType) {
    case "expense":
      return answerValue === null
        ? nullAndAnswered
        : answerValue && (answerValue.amount === null || answerValue.images.length === 0)
          ? returnedObj(answerValue, "pending")
          : answeredWithVal(answerValue);
    case "image":
    case "file":
      return answerValue === null ? nullAndPending : answeredWithVal(answerValue);
    case "number":
    case "decimal":
    case "multiple_choice_one":
    case "single_select":
    case "text":
    case "money":
    case "money_with_negative":
      return answerValue === null || answerValue === undefined || answerValue === ""
        ? nullAndPending
        : answeredWithVal(answerValue);
    case "temperature":
      return answerValue === null
        ? nullAndPending
        : answerValue && answerValue.raw && answerValue.unit && answerValue.kelvin
          ? answeredWithVal(answerValue)
          : answeredButPending(answerValue);
    case "contact":
      return answerValue === null
        ? nullAndPending
        : answerValue && answerValue.name
          ? answeredWithVal(answerValue)
          : answeredButPending(answerValue);
    case "agreement_signature":
      return answerValue === null
        ? nullAndPending
        : answerValue && answerValue.agreed && answerValue.signature
          ? answeredWithVal(answerValue)
          : answeredButPending(answerValue);
    case "toggle":
      return answeredWithVal(answerValue);
    case "date":
      return answerValue ? answeredWithVal(answerValue) : nullAndPending;
    case "location":
      return answerValue ? answeredWithVal(answerValue) : nullAndPending;
    default:
      return returnedObj(answerValue, answerState);
  }
}

export type BombonQuestion = GigReportAnswer<any> & { id: string };

export const getStateId = (qId: string, productId?: string | null) => qId + "_" + (productId || "null");

export default class StepForm extends React.PureComponent<StepFormProps> {
  public renderQuestion = (question: ReportQuestionWChildren, indent: number): React.ReactNode => {
    const {
      step: {
        step: { productId },
        state,
      },
      setAnswer,
      disabled,
    } = this.props;

    const stateId = getStateId(question.id, productId);
    const answer: any = state[stateId];

    return React.createElement(resolveTypeComponent(question.answerType), {
      key: question.id,
      question,
      answer,
      indent,
      disabled,
      setValue: (value, state) => {
        return setAnswer(
          stateId,
          componentStateReducer(question.answerType, value, state || "pending"),
          shouldDebounce(question.answerType),
        );
      },
      childrenQuestions: question.children && question.children.map(q => this.renderQuestion(q, indent + 1)),
    });
  };

  public render() {
    const {
      step: stepState,
      step: { step, questions },
      returnToChecklist,
      verifyReport,
      gig,
      isMobile,
    } = this.props;

    const { name, progress } = step;

    // A pin_output question is answered by the engine, never by a person.
    // ApiNacho already withholds it; this keeps the form safe should one
    // arrive anyway (resolveTypeComponent throws on a type it has no widget for).
    const questionViews = questions
      .filter(q => (q.answerType as string) !== "pin_output")
      .map(q => this.renderQuestion(q, 0));
    const isCheckedIn = (gig && !!gig.checkinStatus) || !gig?.programRole?.role?.hasCheckin;

    return (
      <div className={styles.step}>
        {isMobile ? (
          <StepItem
            title={name}
            progress={progress}
            open
            stepState={stepState}
            onClick={() => returnToChecklist(false)}
          />
        ) : null}

        <div className={cx(styles.stepForm, verifyReport && styles.noBottomPadding)}>
          {CONFIG.steps.allowUpdatesBeforeCheckIn
            ? null
            : !verifyReport && <div className={!isCheckedIn ? styles.overlay : undefined} />}
          <div>
            {step.productId ? <ProductInfoSection productId={step.productId} /> : null}
            <ImageGroup>{questionViews}</ImageGroup>
          </div>
        </div>
      </div>
    );
  }
}

const shouldDebounce = (t: AnswerType) =>
  t === "text" || t === "number" || t === "decimal" || t === "money" || t === "expense" || t === "temperature";

function resolveTypeComponent(answerType: AnswerType): React.ComponentType<QuestionFieldProps<any>> {
  switch (answerType) {
    case "image":
      return ImageQuestionField;
    case "file":
      return FileQuestionField;
    case "multiple_choice_one":
      return MultipleChoiceQuestionField;
    case "single_select":
      return SingleSelectQuestionField;
    case "date":
      return DateQuestionField;
    case "multi_select":
      return MultiSelectQuestionField;
    case "multiple_choice_many":
      return MultipleChoiceManyQuestionField;
    case "atom_choice_one":
      return AtomChoiceOneQuestionField;
    case "atom_choice_many":
      return AtomChoiceMultipleQuestionField;
    case "expense":
      return ExpenseQuestionField;
    case "number":
      return NumberQuestionField;
    case "decimal":
      return DecimalQuestionField;
    case "text":
      return TextQuestionField;
    case "money":
      return MoneyQuestionField;
    case "money_with_negative":
      return MoneyWithNegativeQuestionField;
    case "contact":
      return ContactQuestionField;
    case "agreement_signature":
      return AgreementSignatureQuestionField;
    case "toggle":
      return ToggleQuestionField;
    case "location":
      return LocationQuestionField;
    case "temperature":
      return TemperatureQuestionField;
    case "time":
    case "datetime":
    case "date_range":
    case "time_range":
    case "datetime_range":
    // A geolocate is taken in TheApp, where the browser can ask for a
    // position. This legacy form only has to render without throwing.
    case "geolocate":
      return ReadOnlyQuestionField;
    default:
      throw new Error(`Unimplemented question type: ${answerType} used`);
  }
}

function ReadOnlyQuestionField(props: QuestionFieldProps<unknown>) {
  return (
    <FieldBase {...props} handleNA={false}>
      {null}
    </FieldBase>
  );
}

interface QuestionFieldProps<T> {
  question: ReportQuestion;
  answer: GigReportAnswer<T>;
  setValue: (value: T | T[] | undefined | null, state?: AnswerState | null) => void;
  indent: number;
  disabled: boolean;
  childrenQuestions?: React.ReactNode[];
}

function TextQuestionField(props: QuestionFieldProps<string>) {
  const {
    question,
    setValue,
    disabled,
    answer: { value },
  } = props;
  return (
    <FieldBase {...props}>
      <Form.TextBox
        placeholder={question.inputPlaceholder || "Write here..."}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setValue(e.target.value)}
        value={value || ""}
        multiline
        rows={4}
        disabled={disabled}
      />
    </FieldBase>
  );
}

function NumberQuestionField(props: QuestionFieldProps<number>) {
  const {
    setValue,
    disabled,
    answer: { value },
  } = props;
  return (
    <FieldBase {...props} transparent>
      <Form.NumberBox min={0} onChange={setValue} value={value} disabled={disabled} />
    </FieldBase>
  );
}

const DECIMAL_MIN_PLACES = 1;
const DECIMAL_MAX_PLACES = 12;
const DECIMAL_DEFAULT_PLACES = 2;

// Decimal owns the interpretation of its own config settings (interpreter v1):
// reads maxDecimalPlaces, clamps to [1, 12], defaults to 2.
function decimalMaxPlaces(config?: QuestionConfig | null): number {
  const settings = config?.version === 1 ? (config.settings as DecimalSettings | undefined) : undefined;
  const raw = settings?.maxDecimalPlaces;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return Math.min(DECIMAL_MAX_PLACES, Math.max(DECIMAL_MIN_PLACES, Math.trunc(raw)));
  }
  return DECIMAL_DEFAULT_PLACES;
}

function sanitizeDecimalInput(raw: string, maxPlaces: number): string {
  let s = raw.replace(/[^0-9.-]/g, "");
  s = (s.startsWith("-") ? "-" : "") + s.replace(/-/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  const [intPart, fracPart] = s.split(".");
  return fracPart === undefined ? s : intPart + "." + fracPart.slice(0, maxPlaces);
}

// Render a numeric value to a plain decimal string respecting maxPlaces, without
// scientific notation (which String(value) can produce for very small numbers).
function formatDecimalValue(value: number, maxPlaces: number): string {
  if (!Number.isFinite(value)) {
    return "";
  }
  const fixed = value.toFixed(maxPlaces);
  return fixed.includes(".") ? fixed.replace(/0+$/, "").replace(/\.$/, "") : fixed;
}

function DecimalInput(props: {
  value?: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  placeholder?: string;
  maxDecimalPlaces: number;
}) {
  const { value, onChange, disabled, placeholder, maxDecimalPlaces } = props;
  const [raw, setRaw] = React.useState<string>(
    value === null || value === undefined ? "" : formatDecimalValue(value, maxDecimalPlaces),
  );

  React.useEffect(() => {
    const inProgress = raw === "" || raw === "-" || raw.endsWith(".");
    if (value === null || value === undefined) {
      if (raw !== "" && !inProgress) setRaw("");
    } else if (value !== parseFloat(raw)) {
      const formatted = formatDecimalValue(value, maxDecimalPlaces);
      setRaw(formatted);
      // If the incoming value carried more precision than allowed, reconcile the
      // stored value to what we display so the saved number matches the field.
      const rounded = parseFloat(formatted);
      if (Number.isFinite(rounded) && rounded !== value) {
        onChange(rounded);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = sanitizeDecimalInput(e.target.value, maxDecimalPlaces);
    setRaw(next);
    if (next === "" || next === "-" || next === ".") {
      onChange(null);
    } else {
      const num = parseFloat(next);
      onChange(Number.isNaN(num) ? null : num);
    }
  };

  return (
    <Form.TextBox
      value={raw}
      onChange={handleChange}
      disabled={disabled}
      placeholder={placeholder ?? "–"}
      inputMode="decimal"
    />
  );
}

function DecimalQuestionField(props: QuestionFieldProps<number>) {
  const {
    question,
    setValue,
    disabled,
    answer: { value },
  } = props;
  return (
    <FieldBase {...props} transparent>
      <DecimalInput
        value={value}
        onChange={setValue}
        disabled={disabled}
        placeholder={question.inputPlaceholder || undefined}
        maxDecimalPlaces={decimalMaxPlaces(question.config)}
      />
    </FieldBase>
  );
}

function MoneyQuestionField(props: QuestionFieldProps<number>) {
  const {
    setValue,
    disabled,
    answer: { value },
  } = props;
  return (
    <FieldBase {...props} transparent>
      <Form.DecimalBox onChange={setValue} value={value} disabled={disabled} maxDigits={10} />
    </FieldBase>
  );
}

function MoneyWithNegativeQuestionField(props: QuestionFieldProps<number>) {
  const {
    setValue,
    disabled,
    answer: { value },
  } = props;
  return (
    <FieldBase {...props} transparent>
      <Form.DecimalBox onChange={setValue} value={value} disabled={disabled} allowNegative={true} maxDigits={10} />
    </FieldBase>
  );
}

function MultipleChoiceQuestionField(props: QuestionFieldProps<string>) {
  const {
    setValue,
    disabled,
    answer: { value },
    question,
  } = props;
  const options = question.options ?? [];
  return (
    <FieldBase {...props} transparent>
      <RadioGroup onChange={setValue} value={value}>
        {options.map(opt => (
          <RadioButton key={opt} value={opt} disabled={disabled} testId={question?.key}>
            {opt}
          </RadioButton>
        ))}
      </RadioGroup>
    </FieldBase>
  );
}

function SingleSelectQuestionField(props: QuestionFieldProps<string>) {
  const {
    question,
    setValue,
    disabled,
    answer: { value },
  } = props;
  const options = question.options ?? [];
  return (
    <FieldBase {...props} transparent>
      <SearchableSelect
        selectedItem={value}
        compact
        name={null}
        onChange={e => setValue(e.name, "answered")}
        placeholder={"Select one (or search)"}
        items={options.map(option => ({ id: option, name: option }))}
        wrapperClassName={styles.searchableWrapper}
        inputClassName={styles.customInput}
        disabled={disabled}
      />
    </FieldBase>
  );
}

function MultiSelectQuestionField(props: QuestionFieldProps<string[]>) {
  const {
    question,
    setValue,
    answer: { value },
  } = props;
  const options = question.options ?? [];
  const values = value || [];
  const setFieldValue = (v: string[]) => (v.length ? setValue(v, "answered") : setValue(null, "pending"));

  return (
    <FieldBase {...props} transparent>
      <SearchableMultiSelect
        name={""}
        selectedItems={values}
        compact
        onChange={setFieldValue}
        placeholder={"Select multiple (or search)"}
        items={options.map(option => ({ id: option, name: option }))}
        className={styles.searchableWrapper}
      />
    </FieldBase>
  );
}

function DateQuestionField(props: QuestionFieldProps<string>) {
  const {
    setValue,
    disabled,
    answer: { value },
  } = props;
  return (
    <FieldBase {...props}>
      <CalendarDropdown
        value={value ? parseISO(value) : null}
        onChange={value => setValue(value.toISOString(), "answered")}
        testId={"dateQuestionField"}
        label="Date"
        placeholder="Please select..."
        disabled={disabled}
      />
    </FieldBase>
  );
}

// Known Legacy atoms:
// am_bottle_size
// am_position
// am_program_type
// am_usage_type
// brand_ambassadors
// competitors
// groups
// marketing_initiative
// objects
// platform
// us_states
// vip_chain
// vip_ultimate_owner
// vip_immediate_owner

export interface LegacyAtom<K extends ElementTypeKey> {
  sqid: string;
  element_type_key: K;
  active: boolean;
  name?: string;
  identifier: string;
};

export type ElementTypeKey
  = "account"
  | "account_status"
  | "achievement_type"
  | "am_bottle_size"
  | "am_position"
  | "am_program_type"
  | "am_usage_type"
  | "banner"
  | "brand"
  | "brand_ambassadors"
  | "budget"
  | "budget_legacy"
  | "cocktail"
  | "cocktail_family"
  | "cocktail_ingredient"
  | "color"
  | "complexity"
  | "company"
  | "company_subtype"
  | "company_type"
  | "competitors"
  | "confidence_level"
  | "container_type"
  | "currency"
  | "customer"
  | "division"
  | "event"
  | "event_type"
  | "events"
  | "expense"
  | "expense_category"
  | "file"
  | "flavor_profile"
  | "goal"
  | "goal_category"
  | "goal_type"
  | "glassware"
  | "groups"
  | "label"
  | "location"
  | "location_collection"
  | "manufacturer"
  | "market"
  | "marketing_initiative"
  | "marketing_program"
  | "marketing_tactic"
  | "marketing_theme"
  | "menu_item"
  | "menu_type"
  | "objects"
  | "observation"
  | "observation_type"
  | "opportunity"
  | "package_type"
  | "payable"
  | "payable_type"
  | "payout"
  | "person"
  | "placement_alert_callout"
  | "placement_authorization"
  | "placement_level"
  | "placement_program"
  | "placement_type"
  | "placement_type_velocity"
  | "platform"
  | "pos_item"
  | "pos_type"
  | "premise_type"
  | "product_type"
  | "program"
  | "program_type"
  | "receipt"
  | "sales_motion"
  | "sku"
  | "service_style"
  | "spirit_category"
  | "state"
  | "strength"
  | "tag"
  | "team"
  | "temperature"
  | "templategen"
  | "threat"
  | "unit_size"
  | "unit_type"
  | "us_states"
  | "vip_chain"
  | "vip_immediate_owner"
  | "vip_ultimate_owner"
  | "volume_unit"
  | "weakness";


export interface AtomBase<K extends ElementTypeKey> {
  sqid: string;
  status: AtomStatus;
  updated_at: string;
  element_type_key: K;
};

export interface StandardAtom<K extends ElementTypeKey> extends AtomBase<K> {
  client_identifier?: string;
  display_name: string;
  tag_group_sqid?: string;
  tag_atom_sqids: Array<string>;
}

export interface AccountAtom extends StandardAtom<'account'> {
  name: string;
  status_atom_sqid: string;
  company_atom_sqid: string;
  company_type_group_sqid?: string;
  company_type_atom_sqids: Array<string>;
  company_subtype_group_sqid?: string;
  company_subtype_atom_sqids: Array<string>;
  banner_group_sqid?: string;
  banner_atom_sqids: Array<string>;
  location_group_sqid?: string;
  location_atom_sqids: Array<string>;
  manager_group_sqid?: string;
  manager_atom_sqids: Array<string>;
  contact_group_sqid?: string;
  contact_atom_sqids: Array<string>;
  strength_group_sqid?: string;
  strength_atom_sqids: Array<string>;
  weakness_group_sqid?: string;
  weakness_atom_sqids: Array<string>;
  opportunity_group_sqid?: string;
  opportunity_atom_sqids: Array<string>;
  threat_group_sqid?: string;
  threat_atom_sqids: Array<string>;
  first_sold?: string;
}

export interface AccountStatusAtom extends StandardAtom<'account_status'> {
  status_type: string;
}

export interface AchievementTypeAtom extends StandardAtom<'achievement_type'> {
  name: string;
}

export interface AmBottleSizeAtom extends LegacyAtom<'am_bottle_size'> { }

export interface AmPositionAtom extends LegacyAtom<'am_position'> { }

export interface AmProgramTypeAtom extends LegacyAtom<'am_program_type'> { }

export interface AmUsageTypeAtom extends LegacyAtom<'am_usage_type'> { }

export interface BannerAtom extends StandardAtom<'banner'> {
  name: string;
  location_group_sqid?: string;
  location_atom_sqids: Array<string>;
}

export interface BrandAtom extends AtomBase<'brand'> {
  name: string;
}

export interface BrandAmbassadorsAtom extends LegacyAtom<'brand_ambassadors'> { }

export interface BudgetAtom extends AtomBase<'budget'> {
  name: string;
  amount: number;
  amount_currency_atom_sqid: string;
  editor_group_sqid: string;
  viewer_group_sqid: string;
  spender_group_sqid: string;
  team_group_sqid: string;
  division_group_sqid: string;
  child_budget_group_sqid: string;
  manufacturer_group_sqid: string;
  marketing_program_group_sqid: string;
  parent_budget_sqid?: string;
}

export interface BudgetLegacyAtom extends LegacyAtom<'budget_legacy'> { }

export interface CocktailAtom extends StandardAtom<'cocktail'> {
  name: string;
  cocktail_family_group_sqid?: string;
  cocktail_family_atom_sqids: Array<string>;
  base_spirit_group_sqid?: string;
  base_spirit_atom_sqids: Array<string>;
  photographs_group_sqid?: string;
  photographs_atom_sqids: Array<string>;
  liquid_ingredient_group_sqid?: string;
  liquid_ingredient_atom_sqids: Array<string>;
  other_ingredients?: string;
  garnish_rim?: string;
  preparation_instructions?: string;
  temperature_group_sqid?: string;
  temperature_atom_sqids: Array<string>;
  service_style_group_sqid?: string;
  service_style_atom_sqids: Array<string>;
  glassware_group_sqid?: string;
  glassware_atom_sqids: Array<string>;
  branded_pos_group_sqid?: string;
  branded_pos_atom_sqids: Array<string>;
  complexity_group_sqid?: string;
  complexity_atom_sqids: Array<string>;
  suggested_restaurant_type_group_sqid?: string;
  suggested_restaurant_type_atom_sqids: Array<string>;
  flavor_profile_group_sqid?: string;
  flavor_profile_atom_sqids: Array<string>;
  marketing_theme_group_sqid?: string;
  marketing_theme_atom_sqids: Array<string>;
  color_group_sqid?: string;
  color_atom_sqids: Array<string>;
  developed_by_person_group_sqid?: string;
  developed_by_person_atom_sqids: Array<string>;
  developed_by_company_group_sqid?: string;
  developed_by_company_atom_sqids: Array<string>;
  originally_designed_for_group_sqid?: string;
  originally_designed_for_atom_sqids: Array<string>;
  suggested_retail_price?: number;
  suggested_price_currency_atom_sqid?: string;
  developer_description?: string;
}

export interface CocktailFamilyAtom extends StandardAtom<'cocktail_family'> {
  name: string;
  typical_base_spirit_group_sqid?: string;
  typical_base_spirit_atom_sqids: Array<string>;
}

export interface CocktailIngredientAtom extends StandardAtom<'cocktail_ingredient'> {
  product_type_group_sqid?: string;
  product_type_atom_sqids: Array<string>;
  brand_group_sqid?: string;
  brand_atom_sqids: Array<string>;
  label_group_sqid?: string;
  label_atom_sqids: Array<string>;
  sku_group_sqid?: string;
  sku_atom_sqids: Array<string>;
  volume_unit_atom_sqid?: string;
  volume_amount?: number;
  volume_in_liters?: number;
}

export interface ColorAtom extends StandardAtom<'color'> {
  name: string;
}

export interface ComplexityAtom extends StandardAtom<'complexity'> {
  name: string;
}

export interface CompanyAtom extends StandardAtom<'company'> {
  name: string;
  company_type_group_sqid?: string;
  company_type_atom_sqids: Array<string>;
  company_subtype_group_sqid?: string;
  company_subtype_atom_sqids: Array<string>;
  banner_group_sqid?: string;
  banner_atom_sqids: Array<string>;
  location_group_sqid?: string;
  location_atom_sqids: Array<string>;
  contact_group_sqid?: string;
  contact_atom_sqids: Array<string>;
}

export interface CompanySubtypeAtom extends StandardAtom<'company_subtype'> {
  name: string;
}

export interface CompanyTypeAtom extends StandardAtom<'company_type'> {
  name: string;
}

export interface CompetitorsAtom extends LegacyAtom<'competitors'> { }

export interface ConfidenceLevelAtom extends StandardAtom<'confidence_level'> {
  name: string;
  description?: string;
}

export interface CurrencyAtom extends AtomBase<'currency'> {
  description: string;
  currency: string;
  denomination: string;
  denomination_conversion_ratio: string;
}

export interface CustomerAtom extends AtomBase<'customer'> {
  name: string;
  location_sqids: Array<string>;
  contact_person_sqid?: string;
  contact_person_first_name?: string;
  contact_person_last_name?: string;
  contact_person_email?: string;
}

export interface DivisionAtom extends AtomBase<'division'> {
  name: string;
  state_sqids: Array<string>;
  person_sqids: Array<string>;
}

export interface EventAtom extends StandardAtom<'event'> {
  name: string;
  date?: string;
  description?: string;
  identifier?: string;
}

export interface EventTypeAtom extends StandardAtom<'event_type'> {
  name?: string;
  description?: string;
}

export interface EventsAtom extends LegacyAtom<'events'> { }

export interface ExpenseAtom extends StandardAtom<'expense'> {
  spent_by?: string;
  spent_by_name?: string;
  amount: number;
  currency_type: string;
  currency: string;
  receipt_image_url: Array<string>;
  expense_date?: string;
  expense_category?: string;
  expense_category_name?: string;
  memo: String,
  budget?: string;
  budget_name?: string;
  brand?: string;
  brand_name?: string;
  location?: string;
  location_group_sqid?: string;
  location_atom_sqids: Array<string>;
}

export interface ExpenseCategoryAtom extends AtomBase<'expense_category'> {
  name: string;
}

export interface FileAtom extends StandardAtom<'file'> {
  name: string;
  description?: string;
  url: String;
  filetype?: string;
}

export interface FlavorProfileAtom extends StandardAtom<'flavor_profile'> {
  name: string;
}

export interface GoalCategoryAtom extends StandardAtom<'goal_category'> {
  name: string;
}

export interface GoalTypeAtom extends StandardAtom<'goal_type'> {
  name: string;
  achievement_type_group_sqid?: string;
  achievement_type_atom_sqids: Array<string>;
  goal_category_group_sqid?: string;
}

export interface GlasswareAtom extends StandardAtom<'glassware'> {
  name: string;
}

export interface GroupsAtom extends LegacyAtom<'groups'> { }

export interface LabelAtom extends AtomBase<'label'> {
  name: string;
}

export interface LocationAtom extends AtomBase<'location'> {
  name: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  vip_id?: string;
  td_linx_id?: string;
  created_at: string;
  is_open: boolean;
  client_identifier?: string;
  opened_at?: string;
  closed_at?: string;
  ultimate_owner_atom_sqid?: string;
  immediate_owner_atom_sqid?: string;
  chain_atom_sqid?: string;
  ultimate_owner_name?: string;
  immediate_owner_name?: string;
  chain_name?: string;
}

export interface ManufacturerAtom extends StandardAtom<'manufacturer'> {
  name: string;
}

export interface MarketingInitiativeAtom extends LegacyAtom<'marketing_initiative'> { }

export interface MarketingProgramAtom extends AtomBase<'marketing_program'> {
  name: string;
}

export interface MarketingTacticAtom extends StandardAtom<'marketing_tactic'> {
  name: string;
}

export interface MarketingThemeAtom extends StandardAtom<'marketing_theme'> {
  name: string;
}

export interface MenuItemAtom extends StandardAtom<'menu_item'> {
  price?: number;
  start_date?: string;
  end_date?: string;
  location_group_sqid?: string;
  location_atom_sqids: Array<string>;
  cocktail_group_sqid?: string;
  cocktail_atom_sqids: Array<string>;
  menu_type_group_sqid?: string;
  menu_type_atom_sqids: Array<string>;
  description?: string;
}

export interface MenuTypeAtom extends StandardAtom<'menu_type'> {
  menu_type?: string;
  description?: string;
}

export interface ObjectsAtom extends LegacyAtom<'objects'> { }

export interface ObservationAtom extends StandardAtom<'observation'> {
  observation_date?: string;
  observer_group_sqid?: string;
  observer_atom_sqids: Array<string>;
  observation_type_group_sqid?: string;
  observation_type_atom_sqids: Array<string>;
  menu_item_group_sqid?: string;
  menu_item_atom_sqids: Array<string>;
  description?: string;
}

export interface ObservationTypeAtom extends StandardAtom<'observation_type'> {
  observation_type?: string;
  confidence_level_group_sqid?: string;
  confidence_level_atom_sqids: Array<string>;
  description?: string;
}

export interface OpportunityAtom extends StandardAtom<'opportunity'> {
  file_group_sqid?: string;
  file_atom_sqids: Array<string>;
  name: string;
  description: string;
}

export interface PackageTypeAtom extends StandardAtom<'package_type'> {
  name: string;
}

export interface PersonAtom extends StandardAtom<'person'> {
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface PlacementAlertCalloutAtom extends StandardAtom<'placement_alert_callout'> {
  name: string;
}

export interface PlacementAuthorizationAtom extends StandardAtom<'placement_authorization'> {
  name: string;
}

export interface PlacementLevelAtom extends StandardAtom<'placement_level'> {
  name: string;
}

export interface PlacementProgramAtom extends AtomBase<'placement_program'> {
  name?: string;
  description?: string;
  address_locally: boolean;
  action_needed_detail?: string;
  bottle_size?: string;
  bottle_size_atom_sqid?: string;
  program_type_atom_sqid?: string;
  program_type?: string;
  position_atom_sqid?: string;
  position?: string;
  usage_atom_sqid?: string;
  usage_type?: string;
  label_atom_sqid?: string;
  label_name?: string;
  mandated: boolean;
  featured: boolean;
  featured_start_date?: string;
  featured_end_date?: string;
  track_compliance: boolean;
  program_status?: string;
  liters_per_week?: string;
  start_date?: string;
  end_date?: string;
  artwork?: Array<string>;
  created_at: string;
  locations: Array<string>;
  locations_count?: number;
  duration_ty?: number;
  case_estimate_ty?: string;
  customer_sqid?: string;
  customer_name?: string;
}

export interface PlacementTypeAtom extends StandardAtom<'placement_type'> {
  name: string;
}

export interface PlacementTypeVelocityAtom extends StandardAtom<'placement_type_velocity'> {
  name: string;
}

export interface PlatformAtom extends LegacyAtom<'platform'> { }

export interface PosItemAtom extends StandardAtom<'pos_item'> {
  name: string;
  brand_group_sqid?: string;
  brand_atom_sqids: Array<string>;
  pos_type_group_sqid?: string;
  pos_type_atom_sqids: Array<string>;
}

export interface PosTypeAtom extends StandardAtom<'pos_type'> {
  name: string;
}

export interface PremiseTypeAtom extends AtomBase<'premise_type'> {
  name: string;
}

export interface ProgramAtom extends StandardAtom<'program'> {
  name: string;
}

export interface ProgramTypeAtom extends AtomBase<'program_type'> {
  name: string;
}

export interface StateAtom extends StandardAtom<'state'> {
  name: string;
  code?: string;
}

export interface StrengthAtom extends StandardAtom<'strength'> {
  file_group_sqid?: string;
  file_atom_sqids: Array<string>;
  name: string;
  description: string;
}

export interface PayableAtom extends StandardAtom<'payable'> {
  payer_company_atom_sqid?: string;
  payee_person_atom_sqid?: string;
  amount?: number;
}

export interface PayableTypeAtom extends StandardAtom<'payable_type'> {
  name: string;
}

export interface PayoutAtom extends StandardAtom<'payout'> {
  payee_person_atom_sqid?: string;
  payable_group_sqid?: string;
  payable_atom_sqids: Array<string>;
  stripe_reference: string;
}

export interface ProductTypeAtom extends StandardAtom<'product_type'> {
  name: string;
}

export interface ReceiptAtom extends StandardAtom<'receipt'> {
  name: string;
}

export interface SalesMotionAtom extends StandardAtom<'sales_motion'> {
  name?: string;
  description?: string;
}

export interface SkuAtom extends StandardAtom<'sku'> {
  name: string;
}

export interface ServiceStyleAtom extends StandardAtom<'service_style'> {
  name: string;
}

export interface SpiritCategoryAtom extends StandardAtom<'spirit_category'> {
  name: string;
}

export interface StrengthAtom extends StandardAtom<'strength'> {
  name: string;
}

export interface TagAtom extends StandardAtom<'tag'> {
  name: string;
}

export interface TemperatureAtom extends StandardAtom<'temperature'> {
  name: string;
}

export interface TemplategenAtom extends StandardAtom<'templategen'> { }


export interface ContainerTypeAtom extends StandardAtom<'container_type'> {
  name: string;
}

export interface GoalAtom extends StandardAtom<'goal'> {
  goal_type_atom_sqid?: string;
  name: string;
  value: number;
  brand_group_sqid?: string;
  brand_atom_sqids: Array<string>;
  state_group_sqid?: string;
  state_atom_sqids: Array<string>;
  start_date?: Date;
  end_date?: Date;
}

export interface TeamAtom extends AtomBase<'team'> {
  name: string;
  person_sqids: Array<string>;
}

export interface ThreatAtom extends StandardAtom<'threat'> {
  file_group_sqid?: string;
  file_atom_sqids: Array<string>;
  name: string;
  description: string;
}

export interface UnitSizeAtom extends StandardAtom<'unit_size'> {
  name: string;
}

export interface UnitTypeAtom extends StandardAtom<'unit_type'> {
  name: string;
}

export interface UsStatesAtom extends LegacyAtom<'us_states'> { }

export interface VipChainAtom extends LegacyAtom<'vip_chain'> { }

export interface VipImmediateOwnerAtom extends LegacyAtom<'vip_immediate_owner'> { }

export interface VipUltimateOwnerAtom extends LegacyAtom<'vip_ultimate_owner'> { }

export interface VolumeUnitAtom extends StandardAtom<'volume_unit'> {
  name: string;
}

export interface WeaknessAtom extends StandardAtom<'weakness'> {
  file_group_sqid?: string;
  file_atom_sqids: Array<string>;
  name: string;
  description: string;
}

export interface MarketAtom extends StandardAtom<'market'> {
  name: string;
}

export enum AtomStatus {
  Active = "active",
  Inactive = "inactive",
  Disabled = "disabled",
}

export type Atom =
  | AccountAtom
  | AccountStatusAtom
  | AchievementTypeAtom
  | AmBottleSizeAtom
  | AmPositionAtom
  | AmProgramTypeAtom
  | AmUsageTypeAtom
  | BannerAtom
  | BrandAtom
  | BrandAmbassadorsAtom
  | BudgetAtom
  | BudgetLegacyAtom
  | CocktailAtom
  | CocktailFamilyAtom
  | CocktailIngredientAtom
  | ColorAtom
  | ComplexityAtom
  | CompanyAtom
  | CompanySubtypeAtom
  | CompanyTypeAtom
  | CompetitorsAtom
  | ConfidenceLevelAtom
  | ContainerTypeAtom
  | CurrencyAtom
  | CustomerAtom
  | DivisionAtom
  | EventAtom
  | EventTypeAtom
  | EventsAtom
  | ExpenseAtom
  | ExpenseCategoryAtom
  | FileAtom
  | FlavorProfileAtom
  | GoalAtom
  | GoalCategoryAtom
  | GoalTypeAtom
  | GlasswareAtom
  | GroupsAtom
  | LabelAtom
  | LocationAtom
  | ManufacturerAtom
  | MarketingInitiativeAtom
  | MarketingProgramAtom
  | MarketingTacticAtom
  | MarketingThemeAtom
  | MenuItemAtom
  | MenuTypeAtom
  | ObjectsAtom
  | ObservationAtom
  | ObservationTypeAtom
  | OpportunityAtom
  | PackageTypeAtom
  | PayableAtom
  | PayableTypeAtom
  | PayoutAtom
  | PersonAtom
  | PlacementAlertCalloutAtom
  | PlacementAuthorizationAtom
  | PlacementLevelAtom
  | PlacementProgramAtom
  | PlacementTypeAtom
  | PlacementTypeVelocityAtom
  | PlatformAtom
  | PosItemAtom
  | PosTypeAtom
  | PremiseTypeAtom
  | ProductTypeAtom
  | ProgramAtom
  | ProgramTypeAtom
  | ReceiptAtom
  | SalesMotionAtom
  | SkuAtom
  | ServiceStyleAtom
  | SpiritCategoryAtom
  | StateAtom
  | StrengthAtom
  | TagAtom
  | TeamAtom
  | TemperatureAtom
  | ThreatAtom
  | TemplategenAtom
  | UnitSizeAtom
  | UnitTypeAtom
  | UsStatesAtom
  | VipChainAtom
  | VipImmediateOwnerAtom
  | VipUltimateOwnerAtom
  | VolumeUnitAtom
  | WeaknessAtom
  | MarketAtom;

type Pagination = {
  limit: number;
  offset: number;
};

type LoadAtomsCfg = {
  pagination: Pagination;
  question: ReportQuestion;
  orgId: string;
};

export const atomToValue = (a: AtomBase<ElementTypeKey>): string => a.sqid;

const isDefined = (s?: string): s is string => s !== undefined && s !== ""

const unknownNameLabel = "Unknown name"
const unknownCurrencyLabel = "Unknown currency"

export const legacyAtomToLabel = <K extends ElementTypeKey>(a: LegacyAtom<K>): string => {
  const nameIsDefined = isDefined(a.name);
  const identifierIsDefined = isDefined(a.identifier);

  if (nameIsDefined && identifierIsDefined) {
    return `${a.name} - ${a.identifier}`;
  } else if (nameIsDefined) {
    return `${a.name}`;
  } else if (identifierIsDefined) {
    return `${a.identifier}`;
  } else {
    return unknownNameLabel;
  }
};

// A picker stores a trimmed atom in the answer payload (sqid, name, label,
// display_name, status, updated_at, element_type_key), so first_name/last_name/
// email are absent on everything answered since TheApp took over the form.
export const personAtomToLabel = (a: PersonAtom): string =>
  isDefined(a.first_name) && isDefined(a.last_name)
    ? `${a.first_name} ${a.last_name}`
    : !isDefined(a.first_name) && isDefined(a.last_name)
      ? `${a.last_name}`
      : isDefined(a.email)
        ? `${a.email}`
        : isDefined(a.display_name)
          ? a.display_name
          : `Person ${a.sqid}`;

export const goalAtomToLabel = (a: GoalAtom): string => {
  let label = `${a.name}: ${a.value}`;
  let date = "";
  if (a.start_date && a.end_date) {
    date = `${a.start_date} - ${a.end_date}`;
  } else if (a.start_date) {
    date = `${a.start_date}`;
  } else if (a.end_date) {
    date = `${a.end_date}`;
  }
  return `${label} ${date ? date : ""} `;
}

export const payableAtomToLabel = (a: PayableAtom): string => {
  return `${a.display_name}${a.amount ? `: ${a.amount}` : ""}`;
}

export const payoutAtomToLabel = (a: PayoutAtom): string => {
  return `${a.display_name} - ${a.stripe_reference}`;
}

export const placementProgramAtomToLabel = (a: PlacementProgramAtom): string =>
  isDefined(a.name) ? a.name : `Placement Program ${a.sqid} `;

// TODO: How do we really want to display expenses in a concise way?
export const expenseAtomToLabel = (expenseAtom: ExpenseAtom): string =>
  match(expenseAtom)
    // TODO: Consider also returning the currency atom's
    // denomination_conversion_ratio in the expense atom
    // and using it in the calculation below instead
    .with({ currency_type: "USD" }, a => `$${(a.amount / 100).toFixed(2)} `)
    .otherwise(a => `Amount: ${a.amount}, Currency Type: ${a.currency_type} `);

export const currencyToLabel = (a: CurrencyAtom): string => isDefined(a.currency) ? a.currency : unknownCurrencyLabel;

export const atomWithNameToLabel = <AtomWithName extends { name: string }>(a: AtomWithName): string => isDefined(a.name) ? a.name : unknownNameLabel;

export const accountStatusAtomToLabel = (a: AccountStatusAtom): string =>
  isDefined(a.status_type) ? a.status_type : `Account Status ${a.sqid} `;

export function atomToLabel(a: Atom): string {
  return match(a)
    .with({ element_type_key: "account" }, atomWithNameToLabel)
    .with({ element_type_key: "account_status" }, accountStatusAtomToLabel)
    .with({ element_type_key: "achievement_type" }, atomWithNameToLabel)
    .with({ element_type_key: "am_bottle_size" }, legacyAtomToLabel)
    .with({ element_type_key: "am_position" }, legacyAtomToLabel)
    .with({ element_type_key: "am_program_type" }, legacyAtomToLabel)
    .with({ element_type_key: "am_usage_type" }, legacyAtomToLabel)
    .with({ element_type_key: "banner" }, atomWithNameToLabel)
    .with({ element_type_key: "brand" }, atomWithNameToLabel)
    .with({ element_type_key: "brand_ambassadors" }, legacyAtomToLabel)
    .with({ element_type_key: "budget" }, atomWithNameToLabel)
    .with({ element_type_key: "budget_legacy" }, legacyAtomToLabel)
    .with({ element_type_key: "cocktail" }, atomWithNameToLabel)
    .with({ element_type_key: "cocktail_family" }, atomWithNameToLabel)
    .with({ element_type_key: "cocktail_ingredient" }, (a: CocktailIngredientAtom) => a.display_name)
    .with({ element_type_key: "color" }, atomWithNameToLabel)
    .with({ element_type_key: "complexity" }, atomWithNameToLabel)
    .with({ element_type_key: "company" }, atomWithNameToLabel)
    .with({ element_type_key: "company_subtype" }, atomWithNameToLabel)
    .with({ element_type_key: "company_type" }, atomWithNameToLabel)
    .with({ element_type_key: "competitors" }, legacyAtomToLabel)
    .with({ element_type_key: "confidence_level" }, (a: ConfidenceLevelAtom) => a.display_name)
    .with({ element_type_key: "container_type" }, atomWithNameToLabel)
    .with({ element_type_key: "currency" }, currencyToLabel)
    .with({ element_type_key: "customer" }, atomWithNameToLabel)
    .with({ element_type_key: "division" }, atomWithNameToLabel)
    .with({ element_type_key: "event" }, atomWithNameToLabel)
    .with({ element_type_key: "event_type" }, (a: EventTypeAtom) => a.display_name)
    .with({ element_type_key: "events" }, legacyAtomToLabel)
    .with({ element_type_key: "expense" }, expenseAtomToLabel)
    .with({ element_type_key: "expense_category" }, atomWithNameToLabel)
    .with({ element_type_key: "file" }, atomWithNameToLabel)
    .with({ element_type_key: "flavor_profile" }, atomWithNameToLabel)
    .with({ element_type_key: "goal" }, goalAtomToLabel)
    .with({ element_type_key: "goal_category" }, atomWithNameToLabel)
    .with({ element_type_key: "goal_type" }, atomWithNameToLabel)
    .with({ element_type_key: "glassware" }, atomWithNameToLabel)
    .with({ element_type_key: "groups" }, legacyAtomToLabel)
    .with({ element_type_key: "label" }, atomWithNameToLabel)
    .with({ element_type_key: "location" }, atomWithNameToLabel)
    .with({ element_type_key: "manufacturer" }, atomWithNameToLabel)
    .with({ element_type_key: "market" }, atomWithNameToLabel)
    .with({ element_type_key: "marketing_initiative" }, legacyAtomToLabel)
    .with({ element_type_key: "marketing_program" }, atomWithNameToLabel)
    .with({ element_type_key: "marketing_tactic" }, atomWithNameToLabel)
    .with({ element_type_key: "marketing_theme" }, atomWithNameToLabel)
    .with({ element_type_key: "menu_item" }, (a: MenuItemAtom) => a.display_name)
    .with({ element_type_key: "menu_type" }, (a: MenuTypeAtom) => a.display_name)
    .with({ element_type_key: "objects" }, legacyAtomToLabel)
    .with({ element_type_key: "observation" }, (a: ObservationAtom) => a.display_name)
    .with({ element_type_key: "observation_type" }, (a: ObservationTypeAtom) => a.display_name)
    .with({ element_type_key: "opportunity" }, atomWithNameToLabel)
    .with({ element_type_key: "package_type" }, atomWithNameToLabel)
    .with({ element_type_key: "payable" }, payableAtomToLabel)
    .with({ element_type_key: "payable_type" }, atomWithNameToLabel)
    .with({ element_type_key: "payout" }, payoutAtomToLabel)
    .with({ element_type_key: "person" }, personAtomToLabel)
    .with({ element_type_key: "placement_alert_callout" }, atomWithNameToLabel)
    .with({ element_type_key: "placement_authorization" }, atomWithNameToLabel)
    .with({ element_type_key: "placement_level" }, atomWithNameToLabel)
    .with({ element_type_key: "placement_program" }, placementProgramAtomToLabel)
    .with({ element_type_key: "placement_type" }, atomWithNameToLabel)
    .with({ element_type_key: "placement_type_velocity" }, atomWithNameToLabel)
    .with({ element_type_key: "platform" }, legacyAtomToLabel)
    .with({ element_type_key: "pos_item" }, atomWithNameToLabel)
    .with({ element_type_key: "pos_type" }, atomWithNameToLabel)
    .with({ element_type_key: "premise_type" }, atomWithNameToLabel)
    .with({ element_type_key: "product_type" }, atomWithNameToLabel)
    .with({ element_type_key: "program" }, atomWithNameToLabel)
    .with({ element_type_key: "program_type" }, atomWithNameToLabel)
    .with({ element_type_key: "receipt" }, atomWithNameToLabel)
    .with({ element_type_key: "sales_motion" }, (a: SalesMotionAtom) => a.display_name)
    .with({ element_type_key: "sku" }, atomWithNameToLabel)
    .with({ element_type_key: "service_style" }, atomWithNameToLabel)
    .with({ element_type_key: "spirit_category" }, atomWithNameToLabel)
    .with({ element_type_key: "state" }, atomWithNameToLabel)
    .with({ element_type_key: "strength" }, atomWithNameToLabel)
    .with({ element_type_key: "tag" }, atomWithNameToLabel)
    .with({ element_type_key: "team" }, atomWithNameToLabel)
    .with({ element_type_key: "temperature" }, atomWithNameToLabel)
    .with({ element_type_key: "templategen" }, (t: TemplategenAtom) => t.display_name)
    .with({ element_type_key: "threat" }, atomWithNameToLabel)
    .with({ element_type_key: "unit_size" }, atomWithNameToLabel)
    .with({ element_type_key: "unit_type" }, atomWithNameToLabel)
    .with({ element_type_key: "us_states" }, legacyAtomToLabel)
    .with({ element_type_key: "vip_chain" }, legacyAtomToLabel)
    .with({ element_type_key: "vip_immediate_owner" }, legacyAtomToLabel)
    .with({ element_type_key: "vip_ultimate_owner" }, legacyAtomToLabel)
    .with({ element_type_key: "volume_unit" }, atomWithNameToLabel)
    .with({ element_type_key: "weakness" }, atomWithNameToLabel)
    .otherwise((value: AtomBase<ElementTypeKey> & { display_name?: string }) => value.display_name ?? value.sqid);
}

async function loadPaginatedAtoms(search: string, _: OptionsOrGroups<AtomBase<ElementTypeKey>, GroupBase<AtomBase<ElementTypeKey>>>, loadCfg: LoadAtomsCfg) {
  const { pagination, question, orgId } = loadCfg;

  const qs = new URLSearchParams([
    ...(question.elementSqids ?? []).map(sqid => ["element_sqids", sqid]),
    ...(question.atomTagSqids ?? []).map(tagId => ["atom_sqids", tagId]),
    ["search", search],
    ["limit", pagination.limit.toString()],
    ["offset", pagination.offset.toString()],
    ...(question.filters ? [["filters", JSON.stringify(question.filters)]] : []),
    ["get_for", "report_question"],
  ]);

  const url = `${getRustBackendUrl()}/api/v1/organizations/${orgId}/elements/${question.elementTypeKey}/atoms?${qs.toString()}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getJwt()}` },
    });

    const {
      data,
      pagination: paginationRes,
    }: {
      data: AtomBase<ElementTypeKey>[] | Record<string, AtomBase<ElementTypeKey>[]>;
      pagination: { total_count: number };
    } = await response.json();

    const hasMore = paginationRes.total_count > pagination.offset + pagination.limit;

    const dataArr = Array.isArray(data) ? data : data[question.elementTypeKey ?? ""] ?? [];

    return {
      options: dataArr,
      hasMore,
      additional: {
        ...loadCfg,
        question,
        pagination: {
          limit: pagination.limit,
          offset: pagination.offset + pagination.limit,
        },
      },
    };
  } catch (_) {
    return {
      options: [],
      hasMore: false,
      additional: {
        ...loadCfg,
        question,
        pagination: {
          limit: pagination.limit,
          offset: pagination.offset,
        },
      },
    };
  }
}

type AtomChoiceOneQuestion = { atoms?: AtomBase<ElementTypeKey>[]; other?: string };

function AtomChoiceOneQuestionField(props: QuestionFieldProps<AtomChoiceOneQuestion>) {
  const {
    question,
    setValue,
    answer: { value },
  } = props;
  const [selectedOther, setSelectedOther] = useState<boolean>(value?.other !== undefined);

  useEffect(() => {
    setSelectedOther(value?.other !== undefined);
  }, [value?.other]);

  return (
    <FieldBase {...props} transparent>
      {!selectedOther ? (
        <PaginatedAtomSelector
          question={question}
          value={value?.atoms?.[0]}
          onChange={v => {
            if (isSingleValue(v) && v) {
              setValue({ atoms: [v], other: undefined }, "answered");
            } else {
              setValue({ atoms: undefined, other: undefined }, "pending");
            }
          }}
          isMulti={false}
        />
      ) : null}
      <AtomChoiceQuestionOther
        {...props}
        selectedOther={selectedOther}
        onCheck={v => {
          setSelectedOther(v);
          setValue({ atoms: undefined, other: undefined }, "pending");
        }}
      />
    </FieldBase>
  );
}

function isSingleValue<Atom>(value: SingleValue<Atom> | MultiValue<Atom>): value is SingleValue<Atom> {
  return !Array.isArray(value) && value !== null;
}

type AtomChoiceMultipleQuestion = { atoms?: AtomBase<ElementTypeKey>[]; other?: string };

function AtomChoiceMultipleQuestionField(props: QuestionFieldProps<AtomChoiceMultipleQuestion>) {
  const {
    question,
    setValue,
    answer: { value, state },
  } = props;
  const [selectedOther, setSelectedOther] = useState<boolean>(value?.other !== undefined);

  useEffect(() => {
    setSelectedOther(value?.other !== undefined);
  }, [value?.other]);

  return (
    <FieldBase {...props} transparent>
      <PaginatedAtomSelector
        question={question}
        value={value?.atoms}
        onChange={v => {
          if (!isSingleValue(v) && v.length) {
            setValue({ ...value, atoms: [...v!] }, "answered");
          } else {
            setValue(null, "pending");
          }
        }}
        isMulti
      />
      <AtomChoiceQuestionOther
        {...props}
        selectedOther={selectedOther}
        onCheck={v => {
          setSelectedOther(v);
          if (!v) {
            setValue({ ...value, other: undefined }, state);
          }
        }}
      />
    </FieldBase>
  );
}

type AtomChoiceQuestionOtherProps = {
  selectedOther: boolean;
  onCheck: (v: boolean) => void;
};

function AtomChoiceQuestionOther(
  props: QuestionFieldProps<AtomChoiceOneQuestion | AtomChoiceMultipleQuestion> & AtomChoiceQuestionOtherProps,
) {
  const {
    question,
    disabled,
    answer: { value },
    selectedOther,
    setValue,
    onCheck,
  } = props;

  return (
    <>
      {question.hasOtherOption ? (
        <Form.Checkbox
          onCheck={onCheck}
          disabled={disabled}
          checked={selectedOther}
          className={styles.atomChoiceContainer}
        >
          <span className={styles.atomChoiceOther}>Other</span>
        </Form.Checkbox>
      ) : null}
      {question.hasOtherOption && selectedOther ? (
        <Form.TextBox
          placeholder={question.inputPlaceholder || "Write here..."}
          onChange={({ target: { value: v } }: React.ChangeEvent<HTMLTextAreaElement>) => {
            if (v && v.length > 0) {
              setValue({ ...value, other: v }, "answered");
            } else {
              setValue({ ...value, other: undefined }, "pending");
            }
          }}
          value={value?.other || ""}
          className={styles.atomChoiceOtherInput}
          multiline
          rows={4}
          disabled={disabled}
        />
      ) : null}
    </>
  );
}

type IPaginatedAtomSelector = {
  value?: AtomBase<ElementTypeKey> | AtomBase<ElementTypeKey>[];
  onChange: (v: MultiValue<AtomBase<ElementTypeKey>> | SingleValue<AtomBase<ElementTypeKey>>) => void;
  isMulti?: boolean;
  question: ReportQuestion;
};

const atomIsActive = <K extends ElementTypeKey>(a: AtomBase<K>) => a.status !== AtomStatus.Active;

const legacyAtomIsActive = <K extends ElementTypeKey>(a: LegacyAtom<K>) => !a.active;

function isAtomDisabled(atom: Atom): boolean {
  return match(atom)
    .with({ element_type_key: "account" }, atomIsActive)
    .with({ element_type_key: "account_status" }, atomIsActive)
    .with({ element_type_key: "achievement_type" }, atomIsActive)
    .with({ element_type_key: "am_bottle_size" }, legacyAtomIsActive)
    .with({ element_type_key: "am_position" }, legacyAtomIsActive)
    .with({ element_type_key: "am_program_type" }, legacyAtomIsActive)
    .with({ element_type_key: "am_usage_type" }, legacyAtomIsActive)
    .with({ element_type_key: "banner" }, atomIsActive)
    .with({ element_type_key: "brand" }, atomIsActive)
    .with({ element_type_key: "brand_ambassadors" }, legacyAtomIsActive)
    .with({ element_type_key: "budget" }, atomIsActive)
    .with({ element_type_key: "budget_legacy" }, legacyAtomIsActive)
    .with({ element_type_key: "cocktail" }, atomIsActive)
    .with({ element_type_key: "cocktail_family" }, atomIsActive)
    .with({ element_type_key: "cocktail_ingredient" }, atomIsActive)
    .with({ element_type_key: "color" }, atomIsActive)
    .with({ element_type_key: "complexity" }, atomIsActive)
    .with({ element_type_key: "company" }, atomIsActive)
    .with({ element_type_key: "company_subtype" }, atomIsActive)
    .with({ element_type_key: "company_type" }, atomIsActive)
    .with({ element_type_key: "competitors" }, legacyAtomIsActive)
    .with({ element_type_key: "confidence_level" }, atomIsActive)
    .with({ element_type_key: "container_type" }, atomIsActive)
    .with({ element_type_key: "currency" }, atomIsActive)
    .with({ element_type_key: "customer" }, atomIsActive)
    .with({ element_type_key: "division" }, atomIsActive)
    .with({ element_type_key: "event" }, atomIsActive)
    .with({ element_type_key: "event_type" }, atomIsActive)
    .with({ element_type_key: "events" }, legacyAtomIsActive)
    .with({ element_type_key: "expense" }, atomIsActive)
    .with({ element_type_key: "expense_category" }, atomIsActive)
    .with({ element_type_key: "file" }, atomIsActive)
    .with({ element_type_key: "flavor_profile" }, atomIsActive)
    .with({ element_type_key: "goal" }, atomIsActive)
    .with({ element_type_key: "goal_category" }, atomIsActive)
    .with({ element_type_key: "goal_type" }, atomIsActive)
    .with({ element_type_key: "glassware" }, atomIsActive)
    .with({ element_type_key: "groups" }, legacyAtomIsActive)
    .with({ element_type_key: "label" }, atomIsActive)
    .with({ element_type_key: "location" }, atomIsActive)
    .with({ element_type_key: "manufacturer" }, atomIsActive)
    .with({ element_type_key: "market" }, atomIsActive)
    .with({ element_type_key: "marketing_initiative" }, legacyAtomIsActive)
    .with({ element_type_key: "marketing_program" }, atomIsActive)
    .with({ element_type_key: "marketing_tactic" }, atomIsActive)
    .with({ element_type_key: "marketing_theme" }, atomIsActive)
    .with({ element_type_key: "menu_item" }, atomIsActive)
    .with({ element_type_key: "menu_type" }, atomIsActive)
    .with({ element_type_key: "objects" }, legacyAtomIsActive)
    .with({ element_type_key: "observation" }, atomIsActive)
    .with({ element_type_key: "observation_type" }, atomIsActive)
    .with({ element_type_key: "opportunity" }, atomIsActive)
    .with({ element_type_key: "package_type" }, atomIsActive)
    .with({ element_type_key: "payable" }, atomIsActive)
    .with({ element_type_key: "payable_type" }, atomIsActive)
    .with({ element_type_key: "payout" }, atomIsActive)
    .with({ element_type_key: "person" }, atomIsActive)
    .with({ element_type_key: "placement_alert_callout" }, atomIsActive)
    .with({ element_type_key: "placement_authorization" }, atomIsActive)
    .with({ element_type_key: "placement_level" }, atomIsActive)
    .with({ element_type_key: "placement_program" }, atomIsActive)
    .with({ element_type_key: "placement_type" }, atomIsActive)
    .with({ element_type_key: "placement_type_velocity" }, atomIsActive)
    .with({ element_type_key: "platform" }, legacyAtomIsActive)
    .with({ element_type_key: "pos_item" }, atomIsActive)
    .with({ element_type_key: "pos_type" }, atomIsActive)
    .with({ element_type_key: "premise_type" }, atomIsActive)
    .with({ element_type_key: "product_type" }, atomIsActive)
    .with({ element_type_key: "program" }, atomIsActive)
    .with({ element_type_key: "program_type" }, atomIsActive)
    .with({ element_type_key: "receipt" }, atomIsActive)
    .with({ element_type_key: "sales_motion" }, atomIsActive)
    .with({ element_type_key: "sku" }, atomIsActive)
    .with({ element_type_key: "service_style" }, atomIsActive)
    .with({ element_type_key: "spirit_category" }, atomIsActive)
    .with({ element_type_key: "state" }, atomIsActive)
    .with({ element_type_key: "strength" }, atomIsActive)
    .with({ element_type_key: "tag" }, atomIsActive)
    .with({ element_type_key: "team" }, atomIsActive)
    .with({ element_type_key: "temperature" }, atomIsActive)
    .with({ element_type_key: "templategen" }, atomIsActive)
    .with({ element_type_key: "threat" }, atomIsActive)
    .with({ element_type_key: "unit_size" }, atomIsActive)
    .with({ element_type_key: "unit_type" }, atomIsActive)
    .with({ element_type_key: "us_states" }, legacyAtomIsActive)
    .with({ element_type_key: "vip_chain" }, legacyAtomIsActive)
    .with({ element_type_key: "vip_immediate_owner" }, legacyAtomIsActive)
    .with({ element_type_key: "vip_ultimate_owner" }, legacyAtomIsActive)
    .with({ element_type_key: "volume_unit" }, atomIsActive)
    .with({ element_type_key: "weakness" }, atomIsActive)
    .exhaustive();
}

function PaginatedAtomSelector(props: IPaginatedAtomSelector) {
  const { value, onChange, isMulti, question } = props;

  const {
    match: {
      params: { orgId },
    },
  } = useRouter<{ orgId: string }>();

  const additional: LoadAtomsCfg = {
    pagination: { limit: 20, offset: 0 },
    question,
    orgId,
  };

  return (
    <AsyncPaginate
      additional={additional}
      //The reason to use ! here is that additional prop is defined as optional, but loadOptions has it defined as required.
      //We know that additional will always be defined, so we can safely use ! here.
      loadOptions={(search, atoms, cfg) => loadPaginatedAtoms(search, atoms, cfg!)}
      isOptionDisabled={a => isAtomDisabled(a as Atom)}
      defaultOptions
      getOptionLabel={a => atomToLabel(a as Atom)}
      getOptionValue={atomToValue}
      onChange={onChange}
      value={value ?? null}
      isMulti={isMulti}
      isClearable
      debounceTimeout={300}
      closeMenuOnSelect={!isMulti}
      noOptionsMessage={() => "No items found"}
      styles={{
        menu: provided => ({
          ...provided,
          zIndex: 10,
        }),
      }}
    />
  );
}

function MultipleChoiceManyQuestionField(props: QuestionFieldProps<string[]>) {
  const {
    question,
    setValue,
    disabled,
    answer: { value },
  } = props;
  const options = question.options ?? [];
  const values = value || [];
  const setFieldValue = (v: string[]) => (v.length ? setValue(v, "answered") : setValue(null, "pending"));

  return (
    <FieldBase {...props} transparent>
      <Form.CheckboxGroup onChange={setFieldValue} values={values}>
        {options.map(opt => (
          <Form.Checkbox key={opt} value={opt} disabled={disabled} className={styles.multipleChoiceManyLabel}>
            {opt}
          </Form.Checkbox>
        ))}
      </Form.CheckboxGroup>
    </FieldBase>
  );
}

function FileQuestionField(props: QuestionFieldProps<string[]>) {
  // Same uploader as images, but accepts any file type (file-upload question).
  return <ImageQuestionField {...props} accepts="*/*" />;
}

function ImageQuestionField(props: QuestionFieldProps<string[]> & { accepts?: string }) {
  const {
    question: { optional, notApplicableDesc },
    setValue,
    disabled,
    answer: { value, state },
  } = props;

  const accepts = props.accepts ?? "image";
  const images = value || [];
  const na = state === "na";

  return (
    <FieldBase {...props} transparent handleNA={false}>
      <Form.FilePicker
        size={77}
        required={!optional}
        disabled={na || disabled}
        value={images.map(url => ({ url }))}
        onChange={(images: IFile[]) =>
          setValue(
            images.map(({ url }) => url),
            state,
          )
        }
        onClick={() => {
          if (na) {
            setValue(value, "pending");
          }
        }}
        accepts={accepts}
      >
        {optional
          ? () => (
            <Clickable
              actionLabel={"Not applicable"}
              className={na ? styles.imagesNaActive : styles.imagesNa}
              onClick={() => {
                if (!disabled) setValue(value, "na");
              }}
            >
              <Icon src={cancelIcon} fill={na ? variables.white : variables.gray3} size={20} />
              <Text.H4 kind={na ? "reverse" : "secondary"} className={styles.imageNaText}>
                {notApplicableDesc || "Not Applicable"}
              </Text.H4>
            </Clickable>
          )
          : undefined}
      </Form.FilePicker>
    </FieldBase>
  );
}

interface ExpenseQuestionFieldValue {
  images: IFile[];
  amount: number | null;
}

function ExpenseQuestionField(props: QuestionFieldProps<ExpenseQuestionFieldValue>) {
  const {
    setValue,
    disabled,
    answer: { value, state },
  } = props;

  const onSetMoney = (amount: number | null) =>
    setValue({ images: value ? value.images : [], amount: amount || 0 }, state);
  const onSetImages = (images: IFile[]) => {
    setValue({ amount: value ? value.amount : 0, images }, state);
  };
  const onRadioChange = (val: string | null) => {
    switch (val) {
      case "Yes":
        setValue({ images: [], amount: null }, "pending");
        break;
      case "No":
      default:
        setValue(null, "answered");
        break;
    }
  };

  return (
    <FieldBase {...props} transparent>
      <RadioGroup
        onChange={onRadioChange}
        value={(state === "pending" && value === null) || state === "na" ? null : value !== null ? "Yes" : "No"}
      >
        {["Yes", "No"].map(opt => (
          <RadioButton key={opt} value={opt} disabled={disabled}>
            {opt}
          </RadioButton>
        ))}
      </RadioGroup>
      {value !== null && (
        <div className={styles.expensesContainer}>
          <Form.Section
            header={<Text.H2 className={styles.questionTitle}>What were your total other approved expenses?</Text.H2>}
            description={"Please add up all receipts"}
            transparent={true}
          >
            <Form.DecimalBox onChange={onSetMoney} value={value && value.amount} />
          </Form.Section>
          <Form.Section
            header={<Text.H2 className={styles.questionTitle}>Add photo(s) of receipt(s)</Text.H2>}
            description={"Include itemized receipts and credit card slips. Add captions if photos are unclear"}
            transparent={true}
          >
            <Form.FilePicker
              size={60}
              onChange={onSetImages}
              value={(value && value.images) || []}
              accepts="image"
              caption
            />
          </Form.Section>
        </div>
      )}
      {/*value !== null &&
        value &&
        value.images.map((val, index) => {
          return (
            <ExpenseCard
              expense={value}
              key={value.images[index].url}
              receiptNumber={index + 1}
              className={styles.expenseCard}
            />
          );
              })*/}
    </FieldBase>
  );
}

function ContactQuestionField(props: QuestionFieldProps<ContactQuestionFieldValue>) {
  const {
    question,
    setValue,
    disabled,
    answer: { value },
  } = props;

  const setFieldValue = (fieldName: keyof ContactQuestionFieldValue) => (e: React.ChangeEvent<HTMLInputElement>) => {
    return setValue({
      ...value,
      [fieldName]: fieldName === "phone" ? e.target.value.replace(/\D/g, "") : e.target.value,
    });
  };

  return (
    <FieldBase {...props}>
      <Form.ContactBox
        placeholder={question.inputPlaceholder}
        setFieldValue={setFieldValue}
        value={value || null}
        disabled={disabled}
      />
    </FieldBase>
  );
}

function AgreementSignatureQuestionField(props: QuestionFieldProps<AgreementSignatureQuestionValue>) {
  const {
    setValue,
    answer: { value },
  } = props;

  const toggleAgreed = () =>
    setValue({
      signature: value ? value.signature : "",
      agreed: value ? !value.agreed : true,
    });

  const setSignatureVal = (newVal: string) => setValue({ signature: newVal, agreed: !!(value && value.agreed) });

  return (
    <FieldBase {...props} transparent>
      <Form.AgreementBox
        onSignatureChange={(e: React.ChangeEvent<HTMLInputElement>) => setSignatureVal(e.target.value)}
        onCheck={toggleAgreed}
        value={value || null}
        label={"TYPE YOUR NAME TO CERTIFY THIS REPORT"}
      />
    </FieldBase>
  );
}

function ToggleQuestionField(props: QuestionFieldProps<boolean>) {
  const {
    question,
    setValue,
    answer: { value },
  } = props;

  // A toggle's on/off labels. Missing entries render blank, which is what a
  // label-less toggle (the historical `{}` default) has always done.
  const options = question.options ?? [];

  const setToggleVal = () => setValue(!value);

  return (
    <div className={styles.toggleContainer}>
      <Switch
        id={question.id}
        checked={!!value}
        onChange={setToggleVal}
        className={styles.toggle}
        sliderClassName={value ? styles.toggled : styles.untoggled}
      />

      <div className={cx(value ? styles.toggled : styles.untoggled, styles.toggleLabel)}>
        <Text.Display5>{value ? options[0] : options[1]}</Text.Display5>
      </div>
    </div>
  );
}

function LocationQuestionField(
  props: QuestionFieldProps<{
    address: any;
    latitude: any;
    longitude: any;
    addressJson: google.maps.GeocoderAddressComponent[];
    description: string;
  }>,
) {
  const {
    setValue,
    answer: { value },
  } = props;

  const handleLocationChanged = (suggestion: Suggest) => {
    let locInfo = null;

    if (suggestion && suggestion.gmaps) {
      const {
        location: { lat: latitude, lng: longitude },
        gmaps: { formatted_address: address, address_components },
        label,
      } = suggestion;

      locInfo = {
        address,
        latitude,
        longitude,
        description: label,
        addressJson: address_components,
      };
    }

    setValue(locInfo);
  };

  const handleGeosuggestChanged = (value: string) => {
    if (value.replace(/\s/g, "") === "") {
      setValue(null);
    }
  };

  return (
    <FieldBase {...props} transparent>
      <BaseFormElement
        label={"Address"}
        inputClassNameProp="inputClassName"
        element={
          <Geosuggest
            className={styles.geosuggest}
            placeholder="ex. 6 Harrison Street New York"
            country={[]}
            initialValue={(value && value.description) || ""}
            onChange={handleGeosuggestChanged}
            onSuggestSelect={handleLocationChanged}
          />
        }
      />
    </FieldBase>
  );
}

type Temperature = {
  raw: number;
  unit: TemperatureUnit;
  kelvin: number;
};

function TemperatureQuestionField(props: QuestionFieldProps<Temperature>) {
  const {
    setValue,
    disabled,
    answer: { value },
    question: { inputPlaceholder },
  } = props;

  const [tempError, setTempError] = useState(false);

  const onChange = ({
    raw = value?.raw ?? 0,
    unit = value?.unit ?? TemperatureUnit.Fahrenheit,
  }: Partial<Omit<Temperature, "kelvin">>) => {
    setValue({
      raw,
      unit,
      kelvin: toKelvin(raw, unit),
    });
  };

  const userInputError = (kelvin?: number): boolean => {
    if (typeof kelvin === "number" && (kelvin < 305.37 || kelvin > 314.3)) {
      return true;
    }
    return false;
  };

  const debouncedError = debounce(() => {
    setTempError(userInputError(value?.kelvin));
  }, 400);

  useEffect(() => {
    debouncedError();
  }, [value?.kelvin]);

  return (
    <FieldBase {...props} transparent>
      <div style={{ display: "flex" }}>
        <Form.DecimalBox
          places={1}
          maxDigits={4}
          symbol={null}
          onChange={raw => onChange({ raw })}
          value={value?.raw ?? 0}
          disabled={disabled}
          placeholder={inputPlaceholder ?? "98.6"}
        />
        <Form.Section>
          <Form.Dropdown
            className={styles.temperatureUnit}
            value={value?.unit ?? TemperatureUnit.Fahrenheit}
            onChange={e => onChange({ unit: e.target.value as TemperatureUnit })}
          >
            <option value={TemperatureUnit.Fahrenheit}>&deg;F</option>
            <option value={TemperatureUnit.Celsius}>&deg;C</option>
          </Form.Dropdown>
        </Form.Section>
      </div>
      {tempError && <Text.Message kind={"error"}>Are you sure? Please enter a valid temperature.</Text.Message>}
    </FieldBase>
  );
}

interface FieldBaseProps extends QuestionFieldProps<any> {
  children: React.ReactNode;
  transparent?: boolean;
  handleNA?: boolean;
}

function FieldBase({
  question,
  setValue,
  answer: { state } = { state: "answered" },
  disabled,
  children,
  transparent,
  handleNA = true,
  indent,
  childrenQuestions,
}: FieldBaseProps) {
  return (
    <Desktop>
      {isDesktop => {
        return (
          <div className={styles.question}>
            {indent === 0 ? (
              <svg
                width={isDesktop ? 45 : 35}
                height={20}
                color={state === "pending" ? variables.pink1 : variables.teal1}
                className={styles.questionStatusIndicator}
              >
                <line x1={0} y1={13} x2={isDesktop ? 40 : 30} y2={13} strokeWidth={2} stroke={"currentColor"} />
                <circle cx={isDesktop ? 40 : 30} cy={13} r={5} fill={"currentColor"} />
              </svg>
            ) : (
              <svg width={23} className={styles.questionStatusIndicator}>
                {!isDesktop && <line x1={10} y1={0} x2={10} y2={"100%"} strokeWidth={1} stroke={variables.gray3} />}
                <circle cx={10} cy={13} r={5} fill={state === "pending" ? variables.pink1 : variables.teal1} />
              </svg>
            )}

            <div className={styles.questionField} data-test={`gogetter.report.questionField.${question.key}`}>
              <Form.Section
                header={
                  <Text.H2 className={styles.questionTitle}>
                    <Markdown>{question.gogetterTitle}</Markdown>
                  </Text.H2>
                }
                description={<Markdown>{question.helper}</Markdown>}
                transparent={transparent}
              >
                {children}
              </Form.Section>

              {handleNA && question.optional && (
                <RadioButton
                  selected={state === "na"}
                  onChange={() => setValue(null, "na")}
                  disabled={disabled}
                  className={styles.notApplicable}
                >
                  <Markdown>{question.notApplicableDesc || "Not Applicable"}</Markdown>
                </RadioButton>
              )}

              {question.footer && (
                <Text.P3>
                  <Markdown>{question.footer}</Markdown>
                </Text.P3>
              )}

              <div>{childrenQuestions}</div>

              <div className={styles.questionDiv} />
            </div>
          </div>
        );
      }}
    </Desktop>
  );
}
